import React, { useRef, useState } from 'react';
import './LinkActionPopup.scss';
import Rodal from '../../../components/Rodal/Rodal';
import { Col, ModalBody, ModalHeader, Row } from 'reactstrap';
import ActionDefinitionForm from '../ActionDefinitionForm/ActionDefinitionForm';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import { DocumentDetails } from '../../../slices/marketplace/timeline/models/document-details.model';
import {
    TimelineParty,
    TimelineParties,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineAction } from '../../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { DocumentSection } from '../../../slices/marketplace/timeline/models/document-section.model';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TeaserPracticeData } from '../../../slices/marketplace/timeline/models/teaser-data.model';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { NdaTermSelectionDocumentTemplatesState } from '../../../slices/marketplace/timeline/models/nda-term-selection';
import { EngagementDocumentSource } from '../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { BuyerExpressionOfInterestSettings } from '../../../slices/marketplace/timeline/models/buyer-expression-of-interest-settings.model';
import { LevelOfService } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';

interface LinkActionPopupProps {
    timelineId: number;
    isTemplate: boolean;
    close(): void;
    mode: LinkActionPopupMode;
    party: TimelineParty;
    timelineEvents: TimelineEvent[];
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    getDocumentDetails(documentId?: string): DocumentDetails | null;
    getDataRequestProject(id?: string): LinkedDataRequestProject | null;
    linkAction(
        newAction: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        dataRequestProject?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings
    ): void;
    deleteAction(linkAction: TimelineAction): void;
    setIsConfirmationActionRemovalVisible: (value: boolean) => void;
    documentsSections: DocumentSection[];
    documentDetailsList: DocumentDetails[];
    dataRequestProjects: LinkedDataRequestProject[];
    teaserData: TeaserPracticeData[];
    onTeaserSave: (newValues: TeaserPracticeData[]) => Promise<void>;
    onUpdateConfirm: () => void;
    isTeaserConfirmed: boolean;
    setDocumentSectionList: (documentSections: DocumentSection[]) => void;
    ndaTermSelectionState: NdaTermSelectionDocumentTemplatesState;
    expressionOfInterestSettings: BuyerExpressionOfInterestSettings;
    levelOfService: LevelOfService;
    isMeetingActionEditOrDeleteDisabled?: boolean;
}

export type LinkActionPopupMode = 'create' | 'preview' | 'edit';

const LinkActionPopup = (props: LinkActionPopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [mode, setMode] = useState<LinkActionPopupMode>(props.mode);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const getPartyName = () =>
        TimelineParties.find((f) => f.id === props.party).name;

    const formRef = useRef<any>();

    const renderHeaderText = () => {
        switch (mode) {
            case 'create':
                return 'Link an Action to Timeline Event';
            case 'preview':
                return 'Preview Action linked to Timeline Event';
            case 'edit':
                return 'Edit Action linked to Timeline Event';
        }
    };

    const downloadFile = (
        documentId?: string,
        documentDetails?: DocumentDetails
    ) => {
        let document: DocumentDetails | null = null;

        if (!!documentDetails) {
            document = documentDetails;
        } else if (!!documentId) {
            document = props.getDocumentDetails(documentId);
        } else if (!!props.linkedAction?.referenceItemId) {
            document = props.getDocumentDetails();
        } else {
            return;
        }

        const urlToFetchLink =
            document.isUnregisteredFile && document.temporaryFileId
                ? `api/TemporaryFiles/${document.temporaryFileId}`
                : `/api/marketplace/engagements/${props.timelineId}/documentsDetails/${document.id}`;
        axios
            .get(urlToFetchLink)
            .then((response: any) => {
                window.open(response.data, '_blank');
            })
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while downloading file.',
                    'error'
                )
            );
    };

    const isEditAvailable = () => {
        if (props.isMeetingActionEditOrDeleteDisabled) {
            return false;
        }

        if (
            props.linkedAction?.actionType ===
            TimelineActionType.SellerEngagementDocument
        ) {
            const doc = props.getDocumentDetails(
                props.linkedAction?.referenceItemId
            );
            if (doc?.source === EngagementDocumentSource.Questionnaire) {
                return [TimelineEventStatus.Upcoming].includes(
                    props.currentEvent.status
                );
            }
            return [
                TimelineEventStatus.Upcoming,
                TimelineEventStatus.InProgress,
                TimelineEventStatus.Late,
            ].includes(props.currentEvent.status);
        } else
            return props.currentEvent.status === TimelineEventStatus.Upcoming;
    };

    return (
        <div className="link-action-popup">
            <Rodal
                visible={true}
                onClose={props.close}
                width={600}
                animation={'fade'}
                showMask={true}
                centered>
                <ModalHeader>{renderHeaderText()}</ModalHeader>
                <ModalBody>
                    <Col className="sections-container">
                        <Row className="party-section">
                            <div>
                                <strong>{'Party: '}</strong>
                                {getPartyName()}
                            </div>
                        </Row>
                        <Row className="action-type-section">
                            <div className="section-header">
                                {'Action Type'}
                            </div>
                            <ActionDefinitionForm
                                setIsFileUploading={setIsFileUploading}
                                party={props.party}
                                ref={formRef}
                                timelineEvents={props.timelineEvents}
                                dataRequestProjects={props.dataRequestProjects}
                                onSubmit={props.linkAction}
                                disabled={mode === 'preview'}
                                currentEvent={props.currentEvent}
                                linkedAction={props.linkedAction}
                                getDocumentDetails={props.getDocumentDetails}
                                getDataRequestProject={
                                    props.getDataRequestProject
                                }
                                downloadFile={downloadFile}
                                documentsSections={props.documentsSections}
                                documentDetailsList={props.documentDetailsList}
                                timelineId={props.timelineId}
                                ndaTermSelectionState={
                                    props.ndaTermSelectionState
                                }
                                teaserData={props.teaserData}
                                onTeaserSave={props.onTeaserSave}
                                isTeaserConfirmed={props.isTeaserConfirmed}
                                onUpdateConfirm={() => {
                                    formRef?.current?.handleSubmit();
                                    props.onUpdateConfirm();
                                }}
                                setDocumentSectionList={
                                    props.setDocumentSectionList
                                }
                                isTemplate={props.isTemplate}
                                expressionOfInterestSettings={
                                    props.expressionOfInterestSettings
                                }
                                levelOfService={props.levelOfService}
                            />
                        </Row>
                    </Col>
                    {!isEditAvailable() ? (
                        <div className="buttons-wrapper">
                            <button
                                disabled={false}
                                className="btn btn-outline-secondary"
                                onClick={props.close}>
                                {'Back'}
                            </button>
                        </div>
                    ) : (
                        <div className="buttons-wrapper">
                            <button
                                disabled={false}
                                className="btn btn-outline-secondary"
                                onClick={props.close}>
                                {'Cancel'}
                            </button>
                            {mode === 'create' || mode === 'edit' ? (
                                <ButtonLoader
                                    disabled={false}
                                    onClick={() => {
                                        formRef?.current?.handleSubmit();
                                    }}
                                    className={'btn btn-primary'}
                                    buttonText={'Link Action'}
                                    isLoading={isFileUploading}
                                    loaderButtonText={'Link Action'}
                                />
                            ) : (
                                <></>
                            )}
                            {mode === 'preview' ? (
                                <>
                                    <button
                                        disabled={false}
                                        onClick={() => {
                                            setMode('edit');
                                        }}
                                        className={'btn btn-primary'}>
                                        {'Edit Action'}
                                    </button>
                                    <button
                                        disabled={false}
                                        onClick={() => {
                                            if (
                                                props.linkedAction
                                                    ?.actionType ===
                                                TimelineActionType.SellerEngagementDocument
                                            ) {
                                                const indexOfEventWithRefToThisAction =
                                                    props.timelineEvents.findIndex(
                                                        (e) =>
                                                            e.linkedAction
                                                                ?.actionReferenceId ===
                                                            props.linkedAction
                                                                ?.id
                                                    );
                                                if (
                                                    indexOfEventWithRefToThisAction >
                                                    -1
                                                ) {
                                                    createNotification(
                                                        `You can't delete this Action because it is used in the event with index ${
                                                            indexOfEventWithRefToThisAction +
                                                            1
                                                        }.`,
                                                        'error'
                                                    );
                                                    return;
                                                }
                                            }
                                            props.setIsConfirmationActionRemovalVisible(
                                                true
                                            );
                                        }}
                                        className={'btn btn-primary'}>
                                        {'Delete'}
                                    </button>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default LinkActionPopup;
